import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Wallet,
  TrendingUp,
  TrendingDown,
  BarChart2,
  ArrowRight,
  AlertTriangle,
  CheckCircle2,
  Clock,
} from 'lucide-react';
import { usePMEInvoices } from '../../hooks/usePMEInvoices';
import { usePMEFixedExpenses } from '../../hooks/usePMEFixedExpenses';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';

function formatCurrency(amount: number) {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
  });
}

function getDaysUntil(dateStr: string) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(dateStr);
  target.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

interface KPICardProps {
  label: string;
  value: string;
  sub?: string;
  icon: React.ElementType;
  color: 'blue' | 'green' | 'red' | 'orange';
  onClick?: () => void;
}

function KPICard({ label, value, sub, icon: Icon, color, onClick }: KPICardProps) {
  const colors = {
    blue: { bg: 'bg-blue-50', icon: 'text-blue-600', ring: 'ring-blue-100' },
    green: { bg: 'bg-emerald-50', icon: 'text-emerald-600', ring: 'ring-emerald-100' },
    red: { bg: 'bg-red-50', icon: 'text-red-600', ring: 'ring-red-100' },
    orange: { bg: 'bg-orange-50', icon: 'text-orange-600', ring: 'ring-orange-100' },
  };
  const c = colors[color];

  return (
    <div
      className={`bg-white rounded-xl border border-gray-100 shadow-sm p-6 ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-500 mb-1">{label}</p>
          <p className="text-2xl font-bold text-gray-900 truncate">{value}</p>
          {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
        </div>
        <div className={`${c.bg} ring-4 ${c.ring} rounded-xl p-3 ml-4 flex-shrink-0`}>
          <Icon size={22} className={c.icon} />
        </div>
      </div>
    </div>
  );
}

interface CashFlowPoint {
  label: string;
  balance: number;
  day: number;
}

export function PMEDashboardPage() {
  const navigate = useNavigate();
  const { pendingInvoices, totalPending, loading: invoicesLoading } = usePMEInvoices();
  const { activeExpenses, totalMonthlyBurden, loading: expensesLoading } = usePMEFixedExpenses();

  const loading = invoicesLoading || expensesLoading;

  const projectedBalance30 = useMemo(() => {
    return totalPending - totalMonthlyBurden;
  }, [totalPending, totalMonthlyBurden]);

  const cashFlowData = useMemo((): CashFlowPoint[] => {
    const points: CashFlowPoint[] = [];
    const today = new Date();

    for (let day = 0; day <= 90; day += 7) {
      const date = new Date(today);
      date.setDate(date.getDate() + day);
      const label = date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });

      const inflows = pendingInvoices
        .filter(inv => {
          const payDate = new Date(inv.expected_payment_date);
          return payDate <= date;
        })
        .reduce((sum, inv) => sum + inv.amount_ht * (inv.probability / 100), 0);

      const outflows = activeExpenses
        .filter(exp => {
          const payDate = new Date(exp.next_payment_date);
          return payDate <= date;
        })
        .reduce((sum, exp) => sum + exp.amount, 0);

      const monthsPassed = day / 30;
      const recurringOutflows = activeExpenses
        .filter(e => e.frequency === 'monthly')
        .reduce((sum, e) => sum + e.amount * Math.floor(monthsPassed), 0);

      points.push({
        label,
        balance: inflows - outflows - recurringOutflows,
        day,
      });
    }
    return points;
  }, [pendingInvoices, activeExpenses]);

  const maxAbs = Math.max(...cashFlowData.map(p => Math.abs(p.balance)), 1);

  const upcomingItems = useMemo(() => {
    const invoiceItems = pendingInvoices
      .filter(i => getDaysUntil(i.expected_payment_date) <= 30)
      .map(i => ({
        type: 'invoice' as const,
        label: i.client_name,
        amount: i.amount_ht * (i.probability / 100),
        date: i.expected_payment_date,
        days: getDaysUntil(i.expected_payment_date),
      }));

    const expenseItems = activeExpenses
      .filter(e => getDaysUntil(e.next_payment_date) <= 30)
      .map(e => ({
        type: 'expense' as const,
        label: e.label,
        amount: e.amount,
        date: e.next_payment_date,
        days: getDaysUntil(e.next_payment_date),
      }));

    return [...invoiceItems, ...expenseItems].sort((a, b) => a.days - b.days).slice(0, 6);
  }, [pendingInvoices, activeExpenses]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Cockpit de trésorerie</h2>
          <p className="text-sm text-gray-500 mt-0.5">Vue synthétique de votre situation financière</p>
        </div>
        <div className="text-xs text-gray-400 bg-gray-50 px-3 py-1.5 rounded-full border border-gray-200">
          Aujourd'hui · {new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <KPICard
          label="Encaissements prévus"
          value={formatCurrency(totalPending)}
          sub={`${pendingInvoices.length} facture${pendingInvoices.length > 1 ? 's' : ''} en attente`}
          icon={TrendingUp}
          color="green"
          onClick={() => navigate('/app/pme/invoices')}
        />
        <KPICard
          label="Charges mensuelles"
          value={formatCurrency(totalMonthlyBurden)}
          sub={`${activeExpenses.length} charge${activeExpenses.length > 1 ? 's' : ''} actives`}
          icon={TrendingDown}
          color="red"
          onClick={() => navigate('/app/pme/expenses')}
        />
        <KPICard
          label="Solde net prévisionnel"
          value={formatCurrency(projectedBalance30)}
          sub="Encaissements - charges à 30j"
          icon={Wallet}
          color={projectedBalance30 >= 0 ? 'blue' : 'orange'}
        />
        <KPICard
          label="Charge mensuelle totale"
          value={formatCurrency(totalMonthlyBurden)}
          sub="Toutes fréquences ramenées au mois"
          icon={BarChart2}
          color="orange"
          onClick={() => navigate('/app/pme/expenses')}
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2">
          <Card padding="md">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-base font-semibold text-gray-900">Prévision de trésorerie</h3>
                <p className="text-xs text-gray-500 mt-0.5">Projection sur 90 jours (encaissements - décaissements)</p>
              </div>
            </div>

            {cashFlowData.length === 0 || (totalPending === 0 && totalMonthlyBurden === 0) ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <BarChart2 size={40} className="text-gray-300 mb-3" />
                <p className="text-sm font-medium text-gray-500">Aucune donnée à afficher</p>
                <p className="text-xs text-gray-400 mt-1">Ajoutez des factures ou des charges pour voir votre prévision</p>
                <div className="flex gap-2 mt-4">
                  <Button size="sm" variant="outline" onClick={() => navigate('/app/pme/invoices')}>
                    Ajouter une facture
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => navigate('/app/pme/expenses')}>
                    Ajouter une charge
                  </Button>
                </div>
              </div>
            ) : (
              <div className="relative">
                <div className="flex items-end gap-1 h-48">
                  {cashFlowData.map((point, i) => {
                    const heightPct = Math.abs(point.balance) / maxAbs;
                    const isPositive = point.balance >= 0;
                    return (
                      <div key={i} className="flex-1 flex flex-col items-center justify-end group relative">
                        <div
                          className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10"
                        >
                          {point.label}: {formatCurrency(point.balance)}
                        </div>
                        <div
                          style={{ height: `${Math.max(heightPct * 100, 2)}%` }}
                          className={`w-full rounded-t transition-all duration-500 ${
                            isPositive ? 'bg-emerald-400 hover:bg-emerald-500' : 'bg-red-400 hover:bg-red-500'
                          }`}
                        />
                      </div>
                    );
                  })}
                </div>

                <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                  {cashFlowData
                    .filter((_, i) => i === 0 || i === Math.floor(cashFlowData.length / 2) || i === cashFlowData.length - 1)
                    .map((p, i) => (
                      <span key={i} className="text-xs text-gray-400">{p.label}</span>
                    ))}
                </div>

                <div className="flex items-center gap-4 mt-3">
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded-sm bg-emerald-400"></div>
                    <span className="text-xs text-gray-500">Solde positif</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded-sm bg-red-400"></div>
                    <span className="text-xs text-gray-500">Solde négatif</span>
                  </div>
                </div>
              </div>
            )}
          </Card>
        </div>

        <div>
          <Card padding="md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-gray-900">Prochaines échéances</h3>
              <span className="text-xs text-gray-400 bg-gray-50 px-2 py-1 rounded-full">30 jours</span>
            </div>

            {upcomingItems.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <CheckCircle2 size={32} className="text-emerald-300 mb-2" />
                <p className="text-sm font-medium text-gray-500">Aucune échéance proche</p>
                <p className="text-xs text-gray-400 mt-1">Rien à prévoir dans les 30 prochains jours</p>
              </div>
            ) : (
              <div className="space-y-2">
                {upcomingItems.map((item, i) => (
                  <div
                    key={i}
                    className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer hover:shadow-sm transition-shadow ${
                      item.type === 'invoice' ? 'bg-emerald-50 border-emerald-100' : 'bg-red-50 border-red-100'
                    }`}
                    onClick={() => navigate(item.type === 'invoice' ? '/app/pme/invoices' : '/app/pme/expenses')}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      {item.type === 'invoice' ? (
                        <TrendingUp size={14} className="text-emerald-600 flex-shrink-0" />
                      ) : (
                        <TrendingDown size={14} className="text-red-500 flex-shrink-0" />
                      )}
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-gray-800 truncate">{item.label}</p>
                        <div className="flex items-center gap-1 mt-0.5">
                          <Clock size={10} className="text-gray-400" />
                          <span className="text-xs text-gray-400">{formatDate(item.date)}</span>
                          {item.days <= 7 && (
                            <span className="text-xs font-medium text-orange-600">· urgent</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <span className={`text-xs font-bold flex-shrink-0 ml-2 ${
                      item.type === 'invoice' ? 'text-emerald-700' : 'text-red-600'
                    }`}>
                      {item.type === 'invoice' ? '+' : '-'}{formatCurrency(item.amount)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>

      {projectedBalance30 < 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 flex items-start gap-3">
          <AlertTriangle size={20} className="text-orange-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-orange-800">Attention - Risque de tension de trésorerie</p>
            <p className="text-sm text-orange-700 mt-0.5">
              Vos encaissements prévus ({formatCurrency(totalPending)}) sont inférieurs à vos charges mensuelles ({formatCurrency(totalMonthlyBurden)}).
              Pensez à relancer vos clients ou à décaler certaines dépenses.
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <button
          onClick={() => navigate('/app/pme/invoices')}
          className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-xl hover:border-blue-300 hover:shadow-sm transition-all group"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center">
              <TrendingUp size={20} className="text-emerald-600" />
            </div>
            <div className="text-left">
              <p className="text-sm font-semibold text-gray-900">Factures clients</p>
              <p className="text-xs text-gray-500">{pendingInvoices.length} en attente de paiement</p>
            </div>
          </div>
          <ArrowRight size={16} className="text-gray-400 group-hover:text-blue-600 transition-colors" />
        </button>

        <button
          onClick={() => navigate('/app/pme/expenses')}
          className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-xl hover:border-blue-300 hover:shadow-sm transition-all group"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center">
              <TrendingDown size={20} className="text-red-500" />
            </div>
            <div className="text-left">
              <p className="text-sm font-semibold text-gray-900">Charges fixes</p>
              <p className="text-xs text-gray-500">{activeExpenses.length} charges actives · {formatCurrency(totalMonthlyBurden)}/mois</p>
            </div>
          </div>
          <ArrowRight size={16} className="text-gray-400 group-hover:text-blue-600 transition-colors" />
        </button>
      </div>
    </div>
  );
}
